import React, { createContext, useContext, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  CssBaseline,
  Drawer,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CategoryIcon from '@mui/icons-material/Category';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import EggIcon from '@mui/icons-material/Egg';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import StoreIcon from '@mui/icons-material/Store';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import GroupIcon from '@mui/icons-material/Group';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HubIcon from '@mui/icons-material/Hub';
import PublicIcon from '@mui/icons-material/Public';
import { useAuth } from '../contexts/AuthContext';
import { useAdminScope } from '../contexts/AdminScopeContext';
import { AdminScopeType } from '../models/AdminScope';
import stepBurgerLogo from '../assets/stepburgerLogo.png';

const drawerWidth = 280;
const LayoutDepthContext = createContext(false);

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  requiredScope?: 'group' | 'store';
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'Pilotage',
    items: [
      { text: 'Tableau de bord', icon: <DashboardIcon />, path: '/dashboard' },
      { text: 'Ventes stat', icon: <QueryStatsRoundedIcon />, path: '/sales-statistics' },
      { text: 'Ordres', icon: <ReceiptIcon />, path: '/orders' },
      { text: 'Clients', icon: <GroupIcon />, path: '/customers' },
      { text: 'Analyse Clients', icon: <AnalyticsIcon />, path: '/customer-analytics' },
    ],
  },
  {
    title: 'Catalogue magasin',
    items: [
      { text: 'Catégories', icon: <CategoryIcon />, path: '/categories', requiredScope: 'store' },
      { text: 'Produits', icon: <RestaurantMenuIcon />, path: '/products', requiredScope: 'store' },
      { text: 'Ingrédients menu', icon: <EggIcon />, path: '/menu-ingredients', requiredScope: 'store' },
      { text: 'Customisations', icon: <SettingsIcon />, path: '/customizations', requiredScope: 'store' },
      { text: 'Codes Promo', icon: <LocalOfferIcon />, path: '/promotions', requiredScope: 'store' },
    ],
  },
  {
    title: 'Exploitation groupe',
    items: [
      { text: 'Livreurs', icon: <LocalShippingIcon />, path: '/drivers', requiredScope: 'group' },
      { text: 'Tarifs Livraison', icon: <AttachMoneyIcon />, path: '/delivery-rules', requiredScope: 'group' },
    ],
  },
  {
    title: 'Réseau',
    items: [
      { text: 'Magasins', icon: <StoreIcon />, path: '/stores' },
      { text: 'Paramètres', icon: <AdminPanelSettingsIcon />, path: '/settings' },
    ],
  },
];

interface LayoutProps {
  children: React.ReactNode;
  title: string;
}

const scopeLabel = (scopeType: AdminScopeType) => {
  switch (scopeType) {
    case 'store':
      return 'Magasin';
    case 'group':
      return 'Groupe';
    case 'global':
    default:
      return 'Global';
  }
};

const NestedPageLayout: React.FC<LayoutProps> = ({ children, title }) => {
  const { activeScopeType, selectedGroup, selectedStore } = useAdminScope();

  return (
    <Box>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        <Chip size="small" color="primary" label={`Scope: ${scopeLabel(activeScopeType)}`} />
        {selectedGroup && <Chip size="small" label={`Groupe: ${selectedGroup.name}`} />}
        {selectedStore && <Chip size="small" label={`Magasin: ${selectedStore.name}`} />}
      </Stack>
      {children}
    </Box>
  );
};

const Layout: React.FC<LayoutProps> = ({ children, title }) => {
  const isNestedLayout = useContext(LayoutDepthContext);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const {
    operationGroups,
    stores,
    activeScopeType,
    selectedGroupId,
    selectedStoreId,
    selectedGroup,
    selectedStore,
    availableStoresForSelectedGroup,
    setActiveScopeType,
    setSelectedGroupId,
    setSelectedStoreId,
  } = useAdminScope();
  const scopeBadge = useMemo(() => {
    if (activeScopeType === 'store' && selectedStore) return `Magasin: ${selectedStore.name}`;
    if (activeScopeType === 'group' && selectedGroup) return `Groupe: ${selectedGroup.name}`;
    return 'Réseau global';
  }, [activeScopeType, selectedGroup, selectedStore]);

  if (isNestedLayout) {
    return <NestedPageLayout title={title}>{children}</NestedPageLayout>;
  }

  const handleDrawerToggle = () => {
    setMobileOpen((open) => !open);
  };

  const ensureRequiredScope = (requiredScope?: 'group' | 'store') => {
    if (requiredScope === 'group') {
      if (!selectedGroupId && operationGroups[0]) {
        setSelectedGroupId(operationGroups[0].id);
      }
      setActiveScopeType('group');
    }
    if (requiredScope === 'store') {
      if (!selectedStoreId) {
        const fallbackStore = availableStoresForSelectedGroup[0] || stores[0];
        if (fallbackStore) {
          setSelectedStoreId(fallbackStore.id);
        }
      }
      setActiveScopeType('store');
    }
  };

  const handleNavigation = (item: NavItem) => {
    ensureRequiredScope(item.requiredScope);
    navigate(item.path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
      }}
    >
      <Box
        sx={{
          p: isMobile ? 2 : 3,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          borderBottom: '1px solid rgba(39, 168, 174, 0.1)',
          background: 'linear-gradient(135deg, rgba(39, 168, 174, 0.05) 0%, rgba(46, 179, 184, 0.05) 100%)',
        }}
      >
        <Avatar
          src={stepBurgerLogo}
          alt="Step Burger Logo"
          sx={{
            width: isMobile ? 40 : 56,
            height: isMobile ? 40 : 56,
            mb: 2,
            boxShadow: '0 4px 20px rgba(39, 168, 174, 0.3)',
            border: '2px solid rgba(39, 168, 174, 0.2)',
          }}
        />
        <Typography
          variant={isMobile ? 'subtitle1' : 'h6'}
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #27A8AE 0%, #2EB3B8 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          Step Burger
        </Typography>
        <Chip
          label="Admin"
          size="small"
          sx={{
            mt: 1,
            background: 'linear-gradient(135deg, #27A8AE 0%, #2EB3B8 100%)',
            color: 'white',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
        <Chip
          label={scopeBadge}
          size="small"
          variant="outlined"
          sx={{ mt: 1.5, maxWidth: '100%' }}
        />
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        {navSections.map((section) => (
          <Box key={section.title} sx={{ mb: 1 }}>
            <Typography
              variant="overline"
              sx={{ px: 2.5, color: 'text.secondary', fontWeight: 700 }}
            >
              {section.title}
            </Typography>
            <List sx={{ pt: 0.5 }}>
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                const isScopeMissing =
                  (item.requiredScope === 'group' && operationGroups.length === 0) ||
                  (item.requiredScope === 'store' && stores.length === 0);

                return (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      onClick={() => handleNavigation(item)}
                      disabled={isScopeMissing}
                      sx={{
                        mx: 1,
                        mb: 0.5,
                        borderRadius: 2,
                        minHeight: 48,
                        position: 'relative',
                        overflow: 'hidden',
                        ...(isActive && {
                          background:
                            'linear-gradient(135deg, rgba(39, 168, 174, 0.15) 0%, rgba(46, 179, 184, 0.15) 100%)',
                          '&:before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            background: 'linear-gradient(180deg, #27A8AE 0%, #2EB3B8 100%)',
                            borderRadius: '0 4px 4px 0',
                          },
                        }),
                        '&:hover': {
                          background: isActive
                            ? 'linear-gradient(135deg, rgba(39, 168, 174, 0.2) 0%, rgba(46, 179, 184, 0.2) 100%)'
                            : 'rgba(39, 168, 174, 0.08)',
                          transform: isMobile ? 'none' : 'translateX(4px)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: isActive ? '#27A8AE' : '#4a5568',
                          minWidth: 40,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        secondary={
                          item.requiredScope
                            ? item.requiredScope === 'store'
                              ? 'Magasin'
                              : 'Groupe'
                            : undefined
                        }
                        primaryTypographyProps={{
                          fontSize: isMobile ? '0.875rem' : '0.95rem',
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? '#1a202c' : '#4a5568',
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.72rem',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(39, 168, 174, 0.1)',
          background: 'rgba(248, 250, 252, 0.5)',
        }}
      >
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(239, 68, 68, 0.1) 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.15) 0%, rgba(239, 68, 68, 0.15) 100%)',
            },
          }}
        >
          <ListItemIcon sx={{ color: '#dc2626', minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Déconnexion"
            primaryTypographyProps={{
              fontSize: isMobile ? '0.875rem' : '0.95rem',
              fontWeight: 500,
              color: '#dc2626',
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <LayoutDepthContext.Provider value={true}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Toolbar
            sx={{
              minHeight: { xs: 108, sm: 92 },
              py: { xs: 1, sm: 1.25 },
              px: { xs: 2, sm: 3 },
              gap: 1.5,
              alignItems: { xs: 'flex-start', sm: 'flex-end' },
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  display: { sm: 'none' },
                  p: 1,
                }}
              >
                <MenuIcon />
              </IconButton>

              {isMobile && (
                <Avatar
                  src={stepBurgerLogo}
                  alt="Step Burger Logo"
                  sx={{
                    width: 32,
                    height: 32,
                    boxShadow: '0 2px 8px rgba(39, 168, 174, 0.3)',
                  }}
                />
              )}

              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  flexGrow: 1,
                  fontWeight: 700,
                  fontSize: isMobile ? '1.05rem' : '1.2rem',
                }}
              >
                {title}
              </Typography>
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              sx={{ width: '100%', justifyContent: 'flex-end', alignItems: { xs: 'stretch', md: 'flex-end' } }}
            >
              <Box sx={{ minWidth: { xs: '100%', md: 140 } }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 0.5, px: 0.5, fontWeight: 700, color: 'rgba(255,255,255,0.86)', lineHeight: 1 }}
                >
                  Scope
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={activeScopeType}
                    onChange={(event) => setActiveScopeType(event.target.value as AdminScopeType)}
                    startAdornment={activeScopeType === 'global' ? <PublicIcon sx={{ mr: 1, color: 'text.secondary' }} /> : activeScopeType === 'group' ? <HubIcon sx={{ mr: 1, color: 'text.secondary' }} /> : <StoreIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.92)',
                      color: 'text.primary',
                      '& .MuiSelect-select': { color: 'text.primary' },
                      '& .MuiSvgIcon-root': { color: 'text.secondary' },
                    }}
                  >
                    <MenuItem value="global">Global</MenuItem>
                    <MenuItem value="group">Groupe</MenuItem>
                    <MenuItem value="store">Magasin</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ minWidth: { xs: '100%', md: 200 } }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 0.5, px: 0.5, fontWeight: 700, color: 'rgba(255,255,255,0.86)', lineHeight: 1 }}
                >
                  Groupe
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    displayEmpty
                    value={selectedGroupId || ''}
                    onChange={(event) => setSelectedGroupId(event.target.value || null)}
                    renderValue={(value) => {
                      if (!value) {
                        return <Box sx={{ color: 'text.secondary' }}>Tous les groupes</Box>;
                      }
                      return operationGroups.find((group) => group.id === value)?.name || value;
                    }}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.92)',
                      color: 'text.primary',
                      '& .MuiSelect-select': { color: 'text.primary' },
                      '& .MuiSvgIcon-root': { color: 'text.secondary' },
                    }}
                  >
                    <MenuItem value="">
                      <em>Tous les groupes</em>
                    </MenuItem>
                    {operationGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ minWidth: { xs: '100%', md: 220 } }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 0.5, px: 0.5, fontWeight: 700, color: 'rgba(255,255,255,0.86)', lineHeight: 1 }}
                >
                  Magasin
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    displayEmpty
                    value={selectedStoreId || ''}
                    onChange={(event) => setSelectedStoreId(event.target.value || null)}
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            {selectedGroupId ? 'Tous les magasins du groupe' : 'Tous les magasins'}
                          </Box>
                        );
                      }
                      return availableStoresForSelectedGroup.find((store) => store.id === value)?.name || value;
                    }}
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.92)',
                      color: 'text.primary',
                      '& .MuiSelect-select': { color: 'text.primary' },
                      '& .MuiSvgIcon-root': { color: 'text.secondary' },
                    }}
                  >
                    <MenuItem value="">
                      <em>{selectedGroupId ? 'Tous les magasins du groupe' : 'Tous les magasins'}</em>
                    </MenuItem>
                    {availableStoresForSelectedGroup.map((store) => (
                      <MenuItem key={store.id} value={store.id}>
                        {store.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Toolbar>
        </AppBar>

        <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                border: 'none',
              },
            }}
          >
            {drawer}
          </Drawer>

          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                border: 'none',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            mt: { xs: 10, sm: 11 },
            minHeight: 'calc(100vh - 64px)',
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          }}
        >
          {children}
        </Box>
      </Box>
    </LayoutDepthContext.Provider>
  );
};

export default Layout;
